import { useEffect } from 'react';

import { useDroppable } from '@dnd-kit/core';
import styled from '@emotion/styled';
import { Trans } from '@lingui/react/macro';
import { Grid } from '@mui/material';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useHistory, useLocation, useParams } from 'react-router';

import { SideBySideIcon } from '../../../common-components/elements/VideaIcons/VideaIcons';
import { VideaTypography } from '../../../common-components/elements/VideaTypography/VideaTypography';
import routes from '../../../routes/routes.constants';
import { blueBlack400 } from '../../../theme/videa-style-tokens';
import { useIsEmbedded } from '../../../utils/hooks';
import { selectTeethAtom } from '../../patientList/atoms';
import {
  enabledImageTypesAtom,
  imageBrowserOpenAtom,
} from '../multiFileViewer/findingViewer/imageBrowser/atoms';
import { useGetFilteredImages } from '../multiFileViewer/findingViewer/imageBrowser/helpers';
import type { ImageObject } from '../types';

import { AnnotationSelectedPopup } from './AnnotationSelectedPopup';
import { selectedAnnotationAtom, selectedFindingsAtom } from './atoms';
import { CasePresentationImageViewer } from './CasePresentationImageViewer';
import { DragAndDrop } from './dragAndDrop/DragAndDrop';
import { CasePresentationToolbar } from './toolbar/CasePresentationToolbar';
import { ImageAnnotationTools } from './toolbar/ImageAnnotationTools';

const CasePresentation = styled(Grid)`
  background: black;
  position: relative;
  height: 100%;
  width: 100%;
  justify-content: space-between;
  min-height: 34em;
  min-width: 52em;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  .viewer-layout {
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    height: 100%;
    width: 100%;
    position: relative;
    margin-top: 3.5em;
    .image-container {
      display: flex;
      flex-direction: row;
      // 3.5em (header) + 2.75em (toolbar)
      height: calc(100% - 6.25em);
      margin: 0 4em 0 4em;
      padding-bottom: 2em;
    }
    .image-container-browser-open {
      display: flex;
      flex-direction: row;
      // 3.5em (header) + 5.5em (image browser) + 2.75em (toolbar)
      height: calc(100% - 11.75em);
      margin: 0 4em 0 4em;
      padding-bottom: 2em;
    }
  }
  .drop-box {
    position: absolute;
    top: 1em;
    right: 1em;
    padding: 2em;
    display: flex;
    justify-content: center;
    border: 4px dashed ${blueBlack400};
    color: ${blueBlack400};
    border-radius: 1em;
    height: calc(100% - 18.5em);
    width: 10em;
    .icon {
      position: absolute;
      margin: 0 auto 0 auto;
      width: 2em;
      height: 2em;
      bottom: 12.5em;
      font-size: 1em;
      font-weight: 1em;
    }
    .description {
      position: absolute;
      padding: 2em;
      text-align: center;
      bottom: 0;
      font-size: 1em;
      font-weight: 1em;
    }
  }
  @media (max-height: 768px) {
    min-height: 344px;
    min-width: 100%;
    justify-content: flex-start;
  }
  @media (max-width: 800px) {
    min-height: 400px;
    min-width: 100%;
    justify-content: flex-start;
  }
`;

export const CasePresentationViewer = ({
  activeImage,
  viewerMode,
  imagesArray,
}: {
  activeImage?: ImageObject;
  viewerMode: number;
  imagesArray?: ImageObject[];
}) => {
  // params
  const { id: selectedPatientId } = useParams<{
    id: string;
    imageId?: string;
  }>();

  // atoms
  const [isImageBrowserOpen, setIsImageBrowserOpen] =
    useAtom(imageBrowserOpenAtom);
  const setEnabledImageTypes = useSetAtom(enabledImageTypesAtom);
  const setSelectedAnnotation = useSetAtom(selectedAnnotationAtom);

  const selectedFinding = useAtomValue(selectedFindingsAtom);
  const selectedTeeth = useAtomValue(selectTeethAtom);

  // hooks
  const isEmbedded = useIsEmbedded();
  const history = useHistory();
  const { search } = useLocation();
  const images = useGetFilteredImages({});
  const { setNodeRef } = useDroppable({
    id: 'droppable',
  });

  //variables
  const isValidImage = images?.some(
    (image) => image.imageId === activeImage?.imageId,
  );
  const params = new URLSearchParams(search);
  const firstImage = images?.[0];
  const selectedPhotoId = params.get('selected-photo');
  const selectedPhoto = imagesArray?.find(
    (image) => image.imageId && image.imageId === selectedPhotoId,
  );
  const firstColorImage = images?.find(
    (image) => image.detectedImageType === 'COLOR_IMAGE',
  );

  const shouldAutoSelectDentalPhoto =
    !!selectedFinding || selectedTeeth?.length === 1;

  const imageContainer = isImageBrowserOpen
    ? 'image-container-browser-open'
    : 'image-container';

  const showDropBox = isImageBrowserOpen && !selectedPhoto;

  // handlers
  const handleSetSelectedImage = (id?: string) => {
    if (!id) {
      return;
    }
    const newRoute = isEmbedded
      ? `/embedded${routes.ANNOTATOR}/${selectedPatientId}/${id}`
      : `${routes.ANNOTATOR}/${selectedPatientId}/${id}`;
    history.push(newRoute);
  };

  const handleSetSideBySide = (id?: string) => {
    if (!id) {
      params.delete('selected-photo');
      history.replace({ search: params.toString() });
    } else {
      const searchParams = new URLSearchParams({
        'selected-photo': id,
      });
      history.replace({ search: searchParams.toString() });
    }
  };

  // useEffects
  useEffect(() => {
    if (!activeImage || !isValidImage) {
      handleSetSelectedImage(firstImage?.imageId);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has missing dependencies. Either include it or remove the dependency array. */
  }, [isValidImage, firstImage, handleSetSelectedImage]);

  useEffect(() => {
    if (shouldAutoSelectDentalPhoto) {
      handleSetSideBySide(firstColorImage?.imageId);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has missing dependencies. Either include it or remove the dependency array. */
  }, [shouldAutoSelectDentalPhoto, firstColorImage]);

  useEffect(() => {
    setIsImageBrowserOpen(true);
    setEnabledImageTypes(['dentalPhotos', 'fmxPhotos']);
  }, [setEnabledImageTypes, setIsImageBrowserOpen]);

  useEffect(() => {
    setSelectedAnnotation({ selectedAnnotation: '' });
  }, [activeImage?.imageId, selectedPhoto, setSelectedAnnotation]);

  return (
    <DragAndDrop>
      <CasePresentation>
        <div className="viewer-layout">
          <div className={imageContainer}>
            <ImageAnnotationTools />
            {activeImage && activeImage?.models && (
              <CasePresentationImageViewer
                activeImage={activeImage}
                viewerMode={viewerMode}
              />
            )}
            {selectedPhoto && (
              <CasePresentationImageViewer
                activeImage={selectedPhoto}
                viewerMode={viewerMode}
                isSelectedPhoto
              />
            )}
            <AnnotationSelectedPopup />
            {showDropBox && (
              <div className="drop-box" ref={setNodeRef}>
                <div className="icon">
                  <SideBySideIcon />
                </div>
                <VideaTypography className="description">
                  <Trans>
                    Drag another image from below to view side by side
                  </Trans>
                </VideaTypography>
              </div>
            )}
          </div>
          <CasePresentationToolbar />
        </div>
      </CasePresentation>
    </DragAndDrop>
  );
};
